/**
 * Document Viewer Component
 * Document viewing modal with zoom and controls - responsive & mobile-friendly
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    MagnifyingGlassPlusIcon,
    MagnifyingGlassMinusIcon,
    ArrowsPointingOutIcon,
    DocumentArrowDownIcon,
    XMarkIcon,
    InformationCircleIcon,
    EyeSlashIcon,
    CalendarIcon,
    FolderIcon
} from '@heroicons/react/24/outline';
import {
    convertBase64ToBlobUrl,
    revokeBlobUrl,
    formatFileSize,
    getFileExtensionFromMimeType,
    isImageFile,
    isPdfFile,
    isViewableInline
} from '../../utils/base64Utils';
import { VerificationStatus } from '../../types/verification';

interface DocumentViewerData {
    id: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
    base64Data: string;
    documentType: string;
    category: string;
    status: VerificationStatus;
    uploadedAt: string;
}

interface DocumentViewerProps {
    document: DocumentViewerData | null;
    isOpen: boolean;
    onClose: () => void;
    onDownload?: () => void;
}

type FitMode = 'original' | 'fit-width' | 'fit-height' | 'fit-screen';

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    document,
    isOpen,
    onClose,
    onDownload
}) => {
    const [zoom, setZoom] = useState(100);
    const [showDetails, setShowDetails] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [displayUrl, setDisplayUrl] = useState<string>('');
    const [fitMode, setFitMode] = useState<FitMode>('fit-screen');
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const minZoom = 25;
    const maxZoom = 300;
    const zoomStep = 25;

    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            window.document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            window.document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Convert base64 data to URL for display
    // Use data URL for PDFs (better mobile support), blob URL for images
    useEffect(() => {
        if (document?.base64Data && document?.mimeType) {
            try {
                let url: string;
                
                if (isPdfFile(document.mimeType)) {
                    // For PDFs, create proper data URL
                    const base64Content = document.base64Data.startsWith('data:')
                        ? document.base64Data
                        : `data:application/pdf;base64,${document.base64Data}`;
                    url = base64Content;
                } else {
                    // For images, use blob URL for better memory handling
                    url = convertBase64ToBlobUrl(document.base64Data, document.mimeType);
                }
                
                setDisplayUrl(url);
                setImageError(false);

                // Cleanup function to revoke blob URL only for images
                return () => {
                    if (!isPdfFile(document.mimeType) && url && url.startsWith('blob:')) {
                        revokeBlobUrl(url);
                    }
                };
            } catch (error) {
                console.error('Failed to convert base64 to URL:', error);
                setImageError(true);
            }
        } else {
            setImageError(true);
        }
    }, [document?.base64Data, document?.mimeType]);

    // Calculate zoom based on fit mode
    const calculateFitZoom = useCallback((mode: FitMode) => {
        if (!containerRef.current || !imageRef.current || !document?.mimeType || !isImageFile(document.mimeType)) {
            return 100;
        }

        const container = containerRef.current;
        const img = imageRef.current;

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight - 100; // Account for controls
        const imgNaturalWidth = img.naturalWidth;
        const imgNaturalHeight = img.naturalHeight;

        if (!imgNaturalWidth || !imgNaturalHeight) return 100;

        switch (mode) {
            case 'fit-width':
                return Math.min(Math.floor((containerWidth / imgNaturalWidth) * 100), maxZoom);
            case 'fit-height':
                return Math.min(Math.floor((containerHeight / imgNaturalHeight) * 100), maxZoom);
            case 'fit-screen':
                const scaleX = containerWidth / imgNaturalWidth;
                const scaleY = containerHeight / imgNaturalHeight;
                return Math.min(Math.floor(Math.min(scaleX, scaleY) * 100), maxZoom);
            default:
                return 100;
        }
    }, [document?.mimeType, maxZoom]);

    // Handle initial fit mode when image loads
    useEffect(() => {
        if (displayUrl && document?.mimeType && isImageFile(document.mimeType) && fitMode !== 'original') {
            const img = imageRef.current;
            if (img && img.complete && img.naturalWidth && img.naturalHeight) {
                const newZoom = calculateFitZoom(fitMode);
                setZoom(newZoom);
            }
        }
    }, [displayUrl, fitMode, document?.mimeType, calculateFitZoom]);

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + zoomStep, maxZoom));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - zoomStep, minZoom));
    };

    const handleZoomReset = () => {
        setZoom(100);
        setFitMode('original');
    };

    const handleFitModeChange = (mode: FitMode) => {
        setFitMode(mode);
        if (mode !== 'original') {
            const newZoom = calculateFitZoom(mode);
            setZoom(newZoom);
        } else {
            setZoom(100);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDisplayName = (value: string | undefined) => {
        if (!value) return 'Unknown';
        return value
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (!isOpen || !document) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
                <div
                    className="card relative w-full h-[90vh] flex flex-col rounded-lg shadow-xl overflow-hidden max-w-5xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <FolderIcon className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-600 dark:text-neutral-400 flex-shrink-0" />
                            <div className="min-w-0">
                                <h3 className="font-semibold text-sm sm:text-base text-neutral-900 dark:text-neutral-100 truncate">
                                    {formatDisplayName(document.documentType)}
                                </h3>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                                    {document.fileName}
                                </p>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                    {formatFileSize(document.fileSize)} • {getFileExtensionFromMimeType(document.mimeType)}
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            {onDownload && (
                                <button
                                    onClick={onDownload}
                                    className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                                    title="Download document"
                                >
                                    <DocumentArrowDownIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            )}
                            <button
                                onClick={() => setShowDetails(!showDetails)}
                                className={`p-2 rounded-lg transition-colors ${showDetails
                                    ? 'text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/50'
                                    : 'text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                    }`}
                                title="Toggle document details"
                            >
                                <InformationCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                                title="Close viewer"
                            >
                                <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-col lg:flex-row flex-1 min-h-0">
                        {/* Document Viewer */}
                        <div className="flex-1 flex flex-col min-h-0 lg:min-w-0">
                            {/* Controls - Hidden on very small screens */}
                            <div className="hidden sm:flex items-center justify-between gap-2 p-3 sm:p-4 border-b border-neutral-200 dark:border-neutral-700 flex-wrap">
                                <div className="flex items-center gap-2 sm:gap-3">
                                    {/* Zoom Controls */}
                                    <div className="flex items-center gap-1 sm:gap-2">
                                        <button
                                            onClick={handleZoomOut}
                                            disabled={zoom <= minZoom}
                                            className="p-1.5 sm:p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Zoom out"
                                        >
                                            <MagnifyingGlassMinusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </button>
                                        <span className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 min-w-[50px] sm:min-w-[60px] text-center">
                                            {zoom}%
                                        </span>
                                        <button
                                            onClick={handleZoomIn}
                                            disabled={zoom >= maxZoom}
                                            className="p-1.5 sm:p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Zoom in"
                                        >
                                            <MagnifyingGlassPlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </button>
                                        <button
                                            onClick={handleZoomReset}
                                            className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                                            title="Reset zoom"
                                        >
                                            Reset
                                        </button>
                                    </div>

                                    {/* Fit Mode Controls */}
                                    {document.mimeType && isImageFile(document.mimeType) && (
                                        <div className="flex items-center gap-1 border-l pl-2 sm:pl-4 border-neutral-300 dark:border-neutral-600">
                                            {[
                                                { mode: 'fit-screen' as FitMode, label: 'Fit' },
                                                { mode: 'fit-width' as FitMode, label: 'Width' },
                                                { mode: 'original' as FitMode, label: '1:1' }
                                            ].map(({ mode, label }) => (
                                                <button
                                                    key={mode}
                                                    onClick={() => handleFitModeChange(mode)}
                                                    className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${fitMode === mode
                                                        ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400'
                                                        : 'text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                                        }`}
                                                    title={`${label} view`}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Document Display */}
                            <div ref={containerRef} className="flex-1 overflow-auto bg-neutral-50 dark:bg-neutral-950 p-3 sm:p-4 min-h-0">
                                {imageError ? (
                                    <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400">
                                        <div className="text-center">
                                            <DocumentArrowDownIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4" />
                                            <p className="text-sm sm:text-base mb-1">Unable to display document</p>
                                            <p className="text-xs sm:text-sm">Please use the download button to view the document</p>
                                        </div>
                                    </div>
                                ) : isPdfFile(document.mimeType) ? (
                                    // Use iframe with blob URL for PDF display
                                    <iframe
                                        src={displayUrl}
                                        className="w-full h-full border-0 rounded-lg"
                                        title="PDF Viewer"
                                        style={{ minHeight: '400px' }}
                                    />
                                ) : isImageFile(document.mimeType) ? (
                                    <div className={`flex ${fitMode === 'fit-screen' ? 'items-center justify-center' : 'items-start justify-start'} min-h-full`}>
                                        <img
                                            ref={imageRef}
                                            src={displayUrl}
                                            alt={document.fileName}
                                            onError={() => setImageError(true)}
                                            onLoad={() => {
                                                // Trigger fit mode calculation when image loads
                                                if (fitMode !== 'original') {
                                                    const newZoom = calculateFitZoom(fitMode);
                                                    setZoom(newZoom);
                                                }
                                            }}
                                            className={`transition-transform duration-200 rounded-lg shadow-lg ${fitMode === 'fit-screen' || fitMode === 'fit-width' ? '' : 'max-w-none'
                                                }`}
                                            style={{
                                                transform: `scale(${zoom / 100})`,
                                                transformOrigin: fitMode === 'fit-screen' ? 'center' : 'top left',
                                                maxWidth: fitMode === 'fit-width' ? '100%' : undefined,
                                                height: fitMode === 'fit-height' ? '100%' : 'auto',
                                                objectFit: fitMode === 'fit-screen' ? 'contain' : undefined
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400">
                                        <div className="text-center">
                                            <DocumentArrowDownIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4" />
                                            <p className="text-sm sm:text-base mb-1">Unsupported file type</p>
                                            <p className="text-xs sm:text-sm">This file type cannot be displayed inline</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Document Details Panel - Responsive */}
                        {showDetails && (
                            <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 overflow-y-auto">
                                <div className="p-3 sm:p-4">
                                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                                        <h4 className="font-medium text-sm sm:text-base text-neutral-900 dark:text-neutral-100">
                                            Document Details
                                        </h4>
                                        <button
                                            onClick={() => setShowDetails(false)}
                                            className="p-1 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 rounded transition-colors"
                                            title="Hide document details"
                                        >
                                            <EyeSlashIcon className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Document Information */}
                                    <div className="space-y-3 sm:space-y-4">
                                        {/* Basic Info */}
                                        <div className="p-3 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                            <h5 className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 sm:mb-3 flex items-center gap-2">
                                                <FolderIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                Basic Information
                                            </h5>
                                            <div className="space-y-1.5 sm:space-y-2">
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-xs text-neutral-600 dark:text-neutral-400">Document Type:</span>
                                                    <span className="text-xs text-neutral-900 dark:text-neutral-100 font-medium text-right">
                                                        {formatDisplayName(document.documentType)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-xs text-neutral-600 dark:text-neutral-400">Category:</span>
                                                    <span className="text-xs text-neutral-900 dark:text-neutral-100 font-medium capitalize text-right">
                                                        {document.category}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-xs text-neutral-600 dark:text-neutral-400">File Size:</span>
                                                    <span className="text-xs text-neutral-900 dark:text-neutral-100 font-medium text-right">
                                                        {formatFileSize(document.fileSize)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-xs text-neutral-600 dark:text-neutral-400">File Type:</span>
                                                    <span className="text-xs text-neutral-900 dark:text-neutral-100 font-medium text-right">
                                                        {getFileExtensionFromMimeType(document.mimeType)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Upload Information */}
                                        <div className="p-3 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                            <h5 className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 sm:mb-3 flex items-center gap-2">
                                                <CalendarIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                Upload Information
                                            </h5>
                                            <div className="space-y-1.5 sm:space-y-2">
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-xs text-neutral-600 dark:text-neutral-400">Uploaded At:</span>
                                                    <span className="text-xs text-neutral-900 dark:text-neutral-100 font-medium text-right">
                                                        {formatDate(document.uploadedAt)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-xs text-neutral-600 dark:text-neutral-400">Status:</span>
                                                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${document.status === 'verified' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200' :
                                                        document.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' :
                                                            document.status === 'pending' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200' :
                                                                'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/50 dark:text-neutral-200'
                                                        }`}>
                                                        {formatDisplayName(document.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentViewer;
